import { supabase } from '@/integrations/supabase/client';

export interface FavoriteResult {
  success: boolean;
  isFavorite: boolean;
  error?: string;
}

export const favoriteService = {
  /**
   * Toggle the favorite status of a target
   */
  toggleFavorite: async (
    targetId: string, 
    targetType: 'location' | 'image' | 'prediction',
    currentIsFavorite: boolean
  ): Promise<FavoriteResult> => {
    try {
      const newFavoriteStatus = !currentIsFavorite;
      const table = targetType === 'prediction' ? 'prediction_targets' : 'targets';
      
      const { error } = await supabase
        .from(table)
        .update({ is_favorite: newFavoriteStatus })
        .eq('id', targetId);

      if (error) {
        console.error('❌ favoriteService: Error toggling favorite:', error);
        return { success: false, isFavorite: currentIsFavorite, error: error.message };
      }

      console.log(`✅ favoriteService: Target ${targetId} favorite set to ${newFavoriteStatus}`);
      return { success: true, isFavorite: newFavoriteStatus };
    } catch (error) {
      console.error('❌ favoriteService: Exception toggling favorite:', error);
      return { 
        success: false, 
        isFavorite: currentIsFavorite, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  },

  /**
   * Set favorite status for multiple targets (bulk action)
   */
  bulkSetFavorite: async (
    targets: Array<{ id: string; targetType: 'location' | 'image' | 'prediction' }>,
    isFavorite: boolean
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      // Separate targets by table
      const regularTargetIds = targets
        .filter(t => t.targetType !== 'prediction')
        .map(t => t.id);
      const predictionTargetIds = targets
        .filter(t => t.targetType === 'prediction')
        .map(t => t.id);

      const errors: string[] = [];

      // Update regular targets
      if (regularTargetIds.length > 0) {
        const { error } = await supabase
          .from('targets')
          .update({ is_favorite: isFavorite })
          .in('id', regularTargetIds);

        if (error) {
          errors.push(`Regular targets: ${error.message}`);
        }
      }

      // Update prediction targets
      if (predictionTargetIds.length > 0) {
        const { error } = await supabase
          .from('prediction_targets')
          .update({ is_favorite: isFavorite })
          .in('id', predictionTargetIds);

        if (error) {
          errors.push(`Prediction targets: ${error.message}`);
        }
      }

      if (errors.length > 0) {
        return { success: false, error: errors.join('; ') };
      }

      console.log(`✅ favoriteService: Bulk set ${targets.length} targets to favorite=${isFavorite}`);
      return { success: true };
    } catch (error) {
      console.error('❌ favoriteService: Exception in bulk favorite:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  },

  /**
   * Get favorite status for a target
   */
  getFavoriteStatus: async (
    targetId: string,
    targetType: 'location' | 'image' | 'prediction'
  ): Promise<boolean> => {
    try {
      const table = targetType === 'prediction' ? 'prediction_targets' : 'targets';
      
      const { data, error } = await supabase
        .from(table)
        .select('is_favorite')
        .eq('id', targetId)
        .single();

      if (error || !data) {
        return false;
      }

      return data.is_favorite ?? false;
    } catch (error) {
      console.error('❌ favoriteService: Exception getting favorite status:', error);
      return false;
    }
  }
};
